import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { getGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import {
	FTN_CALLBACK_PATH,
	SMART_ID_TIMEOUT_MS,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useEidEasySign.utils';
import { useEidEasySignCore } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useEidEasySignCore';
import type { EidEasyCompleteRes } from '@dealstack/shared/lib/generated';
import type {
	PrepareResult,
	SigningTransport,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useEidEasySignCore.types';

const SMART_ID_POLL_INTERVAL_MS = 3000;

async function publicSigningApi<T>(path: string, body?: Record<string, unknown>): Promise<T> {
	const config = getGlobalConfig();
	const url = `${config.apiUrl}/public-signing${path}`;
	const response = await fetch(url, {
		method: body ? 'POST' : 'GET',
		headers: body ? { 'Content-Type': 'application/json' } : undefined,
		body: body ? JSON.stringify(body) : undefined,
	});
	if (!response.ok) {
		// "status <code>" is the shape `toErrorKey`'s detector looks for — without it
		// every public-flow failure collapses to the generic error.
		throw new Error(`Public signing API error: status ${response.status}`);
	}
	const data = await response.json();
	return data?.payload ?? data;
}

interface UsePublicEidEasySignOptions {
	token: string;
	onComplete?: () => void;
}

export const usePublicEidEasySign = ({ token, onComplete }: UsePublicEidEasySignOptions) => {
	const ftnMessageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
	const smartIdPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const [selectedMethod, setSelectedMethod] = useState<EidEasySigningMethod | null>(null);

	// Token-based, unauthenticated public transport. A token is always present,
	// so signing can always start.
	const transport = useMemo<SigningTransport>(
		() => ({
			canSign: () => true,
			prepare: (method, extras) =>
				publicSigningApi<PrepareResult>('/prepare', { token, method, ...extras }),
			complete: (docId, extras) =>
				publicSigningApi<EidEasyCompleteRes>('/complete', { token, docId, ...extras }),
		}),
		[token]
	);

	// Public policy: always alert Sentry, tagged `signingFlow: 'public'`.
	const captureError = useCallback((err: unknown, phase: string) => {
		Sentry.captureException(err, { tags: { signingFlow: 'public', phase } });
	}, []);

	const {
		status,
		challengeCode,
		error,
		setStatus,
		setError,
		setChallengeCode,
		unmountedRef,
		pendingContext,
		setPendingContext,
		pendingMethodSelection,
		setPendingMethodSelection,
		capturedSignature,
		setCapturedSignature,
		confirmFreja,
		confirmOtp,
		submitOtpCode,
		resendOtpCode,
		otpVerifying,
		otpCodeError,
		confirmSignature,
		onSignatureConfirmed: coreOnSignatureConfirmed,
		resetOtpContext,
		otpChannel,
		otpMaskedContact,
	} = useEidEasySignCore({
		transport,
		captureError,
		onComplete,
		// Inline panel renders on first paint with no "Sign" button, so the method
		// selector must be visible immediately rather than waiting in IDLE.
		initialStatus: EidEasySignStatus.METHOD_SELECT,
	});

	// Clean up this flow's own listeners/timers on unmount (the core handles its
	// unmount guard separately).
	useEffect(() => {
		return () => {
			if (ftnMessageHandlerRef.current) {
				window.removeEventListener('message', ftnMessageHandlerRef.current);
				ftnMessageHandlerRef.current = null;
			}
			if (smartIdPollIntervalRef.current) {
				clearInterval(smartIdPollIntervalRef.current);
				smartIdPollIntervalRef.current = null;
			}
		};
	}, []);

	const goBack = useCallback(() => {
		setStatus(EidEasySignStatus.METHOD_SELECT);
		setError(null);
		setChallengeCode(null);
		setSelectedMethod(null);
		resetOtpContext();
	}, [setStatus, setError, setChallengeCode, resetOtpContext]);

	// ─── Smart-ID: hosted eID Easy page + poll /complete ─────────────────────

	/** Runs the actual Smart-ID hosted-page flow with the captured signature. */
	const runSmartIdFlow = useCallback(
		async (idcode: string, countryCode: string, signatureBase64: string) => {
			setStatus(EidEasySignStatus.PREPARING);
			try {
				const result = await transport.prepare(EidEasySigningMethod.SMART_ID, {
					drawnSignatureBase64: signatureBase64,
				});

				setStatus(EidEasySignStatus.AWAITING_SIGN);

				const signingPageUrl = `${result.baseUrl}/sign_contract_external?client_id=${result.clientId}&doc_id=${result.docId}&country=${countryCode}&idcode=${idcode}`;

				const startTime = Date.now();
				if (smartIdPollIntervalRef.current) {
					clearInterval(smartIdPollIntervalRef.current);
				}
				const pollInterval = setInterval(async () => {
					if (unmountedRef.current) {
						clearInterval(pollInterval);
						smartIdPollIntervalRef.current = null;
						return;
					}
					if (Date.now() - startTime > SMART_ID_TIMEOUT_MS) {
						clearInterval(pollInterval);
						smartIdPollIntervalRef.current = null;
						setError('eid_easy_signing.error_timeout');
						setStatus(EidEasySignStatus.ERROR);
						return;
					}

					try {
						await transport.complete(result.docId);
						clearInterval(pollInterval);
						smartIdPollIntervalRef.current = null;
						if (unmountedRef.current) {
							return;
						}
						setStatus(EidEasySignStatus.DONE);
						onComplete?.();
					} catch {
						// Not yet signed, continue polling
					}
				}, SMART_ID_POLL_INTERVAL_MS);
				smartIdPollIntervalRef.current = pollInterval;

				const signingTab = window.open(signingPageUrl, '_blank');

				if (!signingTab) {
					// The browser blocked the new tab — surface it immediately so the user
					// knows to allow popups and retry (the error step below shows it too).
					window.alert('Tab blocked. Please allow popups for this site and try again.');
				}
			} catch (err: unknown) {
				captureError(err, 'smart-id-prepare');
				if (unmountedRef.current) {
					return;
				}
				setError('eid_easy_signing.error_generic');
				setStatus(EidEasySignStatus.ERROR);
			}
		},
		[transport, captureError, onComplete, setStatus, setError, unmountedRef]
	);

	/**
	 * Called from the Smart-ID input step after the user's signature has already
	 * been captured in `capturedSignature`. Calls `runSmartIdFlow` directly with
	 * the cached signature — no second SETUP_SIGNATURE pass.
	 */
	const handleConfirmSmartId = useCallback(
		(idcode: string, countryCode: string) => {
			if (!capturedSignature) {
				// No cached signature — fall back to old path.
				setPendingContext({ method: EidEasySigningMethod.SMART_ID, idcode, countryCode });
				setStatus(EidEasySignStatus.SETUP_SIGNATURE);
				return;
			}
			const sig = capturedSignature;
			setCapturedSignature(null);
			void runSmartIdFlow(idcode, countryCode, sig);
		},
		[capturedSignature, setCapturedSignature, setPendingContext, setStatus, runSmartIdFlow]
	);

	// ─── FTN: hosted eID Easy page in a popup + message-based callback ────────

	/** Runs the actual FTN popup flow with the captured signature. */
	const runFtnFlow = useCallback(
		async (signatureBase64: string) => {
			setStatus(EidEasySignStatus.PREPARING);
			try {
				const callbackUrl = `${window.location.origin}${FTN_CALLBACK_PATH}`;
				const result = await transport.prepare(EidEasySigningMethod.FTN, {
					signatureRedirect: callbackUrl,
					drawnSignatureBase64: signatureBase64,
				});

				const signingPageUrl = `${result.baseUrl}/sign_contract_external?client_id=${result.clientId}&doc_id=${result.docId}`;
				window.open(signingPageUrl, 'eid-easy-ftn', 'width=600,height=700');

				setStatus(EidEasySignStatus.AWAITING_SIGN);

				const handleMessage = async (event: MessageEvent) => {
					if (event.data?.type === 'eid-easy-ftn-complete') {
						window.removeEventListener('message', handleMessage);
						ftnMessageHandlerRef.current = null;
						if (unmountedRef.current) {
							return;
						}
						setStatus(EidEasySignStatus.COMPLETING);
						try {
							await transport.complete(result.docId);
							if (unmountedRef.current) {
								return;
							}
							setStatus(EidEasySignStatus.DONE);
							onComplete?.();
						} catch (err: unknown) {
							captureError(err, 'ftn-complete');
							if (unmountedRef.current) {
								return;
							}
							setError('eid_easy_signing.error_generic');
							setStatus(EidEasySignStatus.ERROR);
						}
					}
				};

				if (ftnMessageHandlerRef.current) {
					window.removeEventListener('message', ftnMessageHandlerRef.current);
				}
				ftnMessageHandlerRef.current = handleMessage;
				window.addEventListener('message', handleMessage);
			} catch (err: unknown) {
				captureError(err, 'ftn-prepare');
				if (unmountedRef.current) {
					return;
				}
				setError('eid_easy_signing.error_generic');
				setStatus(EidEasySignStatus.ERROR);
			}
		},
		[transport, captureError, onComplete, setStatus, setError, unmountedRef]
	);

	// ─── Shared signature-confirmed handler (wraps core + adds Smart-ID/FTN) ──

	const handleSignatureConfirmed = useCallback(
		async (signatureBase64: string) => {
			// If the user came via method-select → SETUP_SIGNATURE, handle FTN here.
			// Smart-ID, OTP, Freja advance to their input steps via the core.
			if (pendingMethodSelection === EidEasySigningMethod.FTN) {
				setPendingMethodSelection(null);
				await runFtnFlow(signatureBase64);
				return;
			}

			// If pendingContext.method is Smart-ID or FTN (old path), handle those here.
			if (pendingContext?.method === EidEasySigningMethod.SMART_ID) {
				const ctx = pendingContext;
				setPendingContext(null);
				await runSmartIdFlow(ctx.idcode, ctx.countryCode, signatureBase64);
				return;
			}
			if (pendingContext?.method === EidEasySigningMethod.FTN) {
				setPendingContext(null);
				await runFtnFlow(signatureBase64);
				return;
			}

			// Delegate all other cases to the core handler.
			await coreOnSignatureConfirmed(signatureBase64);
		},
		[
			pendingMethodSelection,
			setPendingMethodSelection,
			pendingContext,
			setPendingContext,
			runSmartIdFlow,
			runFtnFlow,
			coreOnSignatureConfirmed,
		]
	);

	const handleSelectMethod = useCallback(
		(method: EidEasySigningMethod) => {
			// Clear any cached signature from a previous attempt.
			setCapturedSignature(null);
			setSelectedMethod(method);

			if (method === EidEasySigningMethod.DRAW) {
				resetOtpContext();
				setStatus(EidEasySignStatus.DRAW_SIGNATURE);
				return;
			}

			// All non-DRAW methods gate through SETUP_SIGNATURE immediately on method
			// select, before any method-specific input step.
			setPendingMethodSelection(method);
			setStatus(EidEasySignStatus.SETUP_SIGNATURE);
		},
		[setCapturedSignature, resetOtpContext, setPendingMethodSelection, setStatus]
	);

	return {
		status,
		challengeCode,
		error,
		selectedMethod,
		isSigningInProgress:
			status === EidEasySignStatus.PREPARING ||
			status === EidEasySignStatus.AWAITING_SIGN ||
			status === EidEasySignStatus.COMPLETING,
		goBack,
		handleSelectMethod,
		handleConfirmSmartId,
		handleConfirmSignature: confirmSignature,
		handleSignatureConfirmed,
		handleConfirmFreja: confirmFreja,
		handleConfirmOtp: confirmOtp,
		handleSubmitOtpCode: submitOtpCode,
		handleResendOtpCode: resendOtpCode,
		otpVerifying,
		otpCodeError,
		otpChannel,
		otpMaskedContact,
	};
};
